import { Box, Typography } from '@mui/material';
import DeformIcon from '~/assets/tool-icons/deform.svg?react';
import LineDeformIcon from '~/assets/tool-icons/line_deform.svg?react';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
    AdaptiveBrushIcon,
    BrushIcon,
    EraseIcon,
    LineIcon,
    TransformIcon,
} from '~/assets/workspace-icons';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import ToolPropertiesTray from '~/components/toolbar/shared/ToolPropertiesTray';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import {
    selectedROIState,
    selectedRoiIsPointTypeState,
} from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    brushOptionsState,
    lineOptionsState,
    selectedToolState,
} from '~/state/atoms/toolsAtoms';
import BrushToolOptions from './BrushToolOptions';
import DeformToolOptions from './DeformToolOptions';
import LineDeformToolOptions from './LineDeformToolOptions';
import LineToolOptions from './LineToolOptions';
import TransformToolOptions from './TransformToolOptions';

export default function ContouringTools() {
    const intl = useIntl();
    const [tool, setTool] = useRecoilState(selectedToolState);
    const selectedRoiNumber = useRecoilValue(selectedROIState);
    const isPointRoi = useRecoilValue(selectedRoiIsPointTypeState);
    const primaryImage = useRecoilValue(primaryImageState);
    const selectedStructureSet = useRecoilValue(currentStructureSetState);
    const [brushOptions, setBrushOptions] = useRecoilState(brushOptionsState);
    const [lineOptions, setLineOptions] = useRecoilState(lineOptionsState);
    const [validationError, setValidationError] = useState<string | null>(null);

    const isTilted = !!primaryImage?.firstSlice.tilted;

    const noRoiSelected =
        !selectedStructureSet ||
        selectedStructureSet.roiArray.length === 0 ||
        selectedRoiNumber === -1;

    const selectedRoi = noRoiSelected
        ? undefined
        : selectedStructureSet.roiArray.find(
              (r) => r.roiNumber === selectedRoiNumber
          );

    const isApproved =
        !!selectedStructureSet?.isApproved || !!selectedRoi?.isApproved;

    const isContouringToolsDisabled =
        noRoiSelected || isPointRoi || isTilted || isApproved;

    const isMoveTransformDisabled = noRoiSelected || isTilted || isApproved;

    /**
     * When a contouring tool is disabled, return a short reason
     * that can be appended to the tooltip so the user knows why.
     * Returns undefined when the tool isn't disabled (caller falls
     * back to the plain tool name).
     */
    const disabledReason = useMemo<string | undefined>(() => {
        if (isApproved) {
            return intl.formatMessage({ id: 'tool.disabled.approved' });
        }
        if (isTilted) {
            return intl.formatMessage({ id: 'tool.disabled.oblique' });
        }
        if (isPointRoi) {
            return intl.formatMessage({ id: 'tool.disabled.pointRoi' });
        }
        if (noRoiSelected) {
            return intl.formatMessage({ id: 'tool.disabled.noRoi' });
        }
        return undefined;
    }, [intl, isApproved, isTilted, isPointRoi, noRoiSelected]);

    const tooltipFor = (toolName: string) =>
        disabledReason
            ? intl.formatMessage(
                  { id: 'tool.disabledTooltip' },
                  { toolName, reason: disabledReason }
              )
            : toolName;

    const isStaticBrushSelected =
        tool === 'brush' &&
        brushOptions.mode === 'static' &&
        !brushOptions.erase;
    const isAdaptiveBrushSelected =
        tool === 'brush' &&
        brushOptions.mode === 'smart' &&
        !brushOptions.erase;
    const isLineDrawSelected = tool === 'line' && lineOptions.mode === 'draw';
    const isEraseSelected = tool === 'brush' && brushOptions.erase;
    const isTransformSelected = tool === 'transform' || tool === 'poi-move';
    const isLineDeformSelected = tool === 'lineDeform';

    useEffect(() => {
        if (selectedRoiNumber === -1 || isApproved) {
            setTool('pan');
            return;
        }

        setTool((prev) => {
            if (isPointRoi) {
                if (prev === 'transform' || prev === 'poi-move') {
                    return 'poi-move';
                }
                return 'pan';
            } else {
                if (prev === 'poi-move') {
                    return 'transform';
                }
            }
            return prev;
        });
    }, [selectedRoiNumber, isPointRoi, isApproved, setTool]);

    useEffect(() => {
        if (validationError) {
            enqueueSnackbar(validationError, { variant: 'error' });
            setValidationError(null);
        }
    }, [validationError]);

    const validateBrush = (): boolean => {
        if (primaryImage?.firstSlice.tilted) {
            setValidationError(
                'Brush tool is not available for tilted datasets'
            );
            return false;
        }
        return true;
    };

    const handleStaticBrushSelect = () => {
        if (isStaticBrushSelected) {
            setTool('pan');
            return;
        }
        if (!validateBrush()) {
            return;
        }
        setTool('brush');
        setBrushOptions((options) => ({
            ...options,
            mode: 'static',
            erase: false,
        }));
    };

    const handleAdaptiveBrushSelect = () => {
        if (isAdaptiveBrushSelected) {
            setTool('pan');
            return;
        }
        if (!validateBrush()) {
            return;
        }
        setTool('brush');
        setBrushOptions((options) => ({
            ...options,
            mode: 'smart',
            erase: false,
        }));
    };

    const handleLineSelect = () => {
        if (isLineDrawSelected) {
            setTool('pan');
            return;
        }
        if (primaryImage?.firstSlice.tilted) {
            setValidationError(
                'Line draw tool is not available for tilted datasets'
            );
            return;
        }
        setTool('line');
        setLineOptions((options) => ({ ...options, mode: 'draw' }));
    };

    const handleEraseSelect = () => {
        if (isEraseSelected) {
            setTool('pan');
            return;
        }
        if (!validateBrush()) {
            return;
        }
        setTool('brush');
        setBrushOptions((options) => ({
            ...options,
            mode: 'static', // Erase mode uses the same brush behavior as static, just with the erase flag on
            erase: true,
        }));
    };

    const handleTransformSelect = useCallback(() => {
        if (isTransformSelected) {
            setTool('pan');
            return;
        }
        setTool(isPointRoi ? 'poi-move' : 'transform');
    }, [setTool, isPointRoi, isTransformSelected]);

    const handleLineDeformSelect = () => {
        if (isLineDeformSelected) {
            setTool('pan');
            return;
        }
        if (!validateBrush()) {
            return;
        }
        setTool('lineDeform');
    };

    const handleDeformSelect = () => {
        if (tool === 'deform') {
            setTool('pan');
            return;
        }
        if (!validateBrush()) {
            return;
        }
        setTool('deform');
    };

    const isSelectedSupport =
        selectedRoi?.interpretedType === RoiInterpretedType.SUPPORT;
    const hasSiblingSupport = useMemo(() => {
        if (!isSelectedSupport || !selectedStructureSet) {
            return false;
        }
        return selectedStructureSet.roiArray.some(
            (r) =>
                r.roiNumber !== selectedRoiNumber &&
                r.interpretedType === RoiInterpretedType.SUPPORT
        );
    }, [isSelectedSupport, selectedStructureSet, selectedRoiNumber]);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                }}
            >
                <ToolbarButton
                    icon={<BrushIcon />}
                    tooltip={tooltipFor('Static Brush')}
                    tooltipPlacement="bottom"
                    selected={isStaticBrushSelected}
                    disabled={isContouringToolsDisabled}
                    onClick={handleStaticBrushSelect}
                    data-testid="tool-static-brush"
                />

                <ToolbarButton
                    icon={<AdaptiveBrushIcon />}
                    tooltip={tooltipFor('Adaptive Brush')}
                    tooltipPlacement="bottom"
                    selected={isAdaptiveBrushSelected}
                    disabled={isContouringToolsDisabled}
                    onClick={handleAdaptiveBrushSelect}
                    data-testid="tool-adaptive-brush"
                />

                <ToolbarButton
                    icon={<LineIcon />}
                    tooltip={tooltipFor('Line Draw')}
                    tooltipPlacement="bottom"
                    selected={isLineDrawSelected}
                    disabled={isContouringToolsDisabled}
                    onClick={handleLineSelect}
                    data-testid="tool-line-draw"
                />

                <ToolbarButton
                    icon={<EraseIcon />}
                    tooltip={tooltipFor('Erase')}
                    tooltipPlacement="bottom"
                    selected={isEraseSelected}
                    disabled={isContouringToolsDisabled}
                    onClick={handleEraseSelect}
                    data-testid="tool-erase"
                />
                <ToolbarButton
                    icon={<TransformIcon />}
                    tooltip={
                        isMoveTransformDisabled && disabledReason ? (
                            tooltipFor(
                                isPointRoi ? 'Move POI' : 'Transform Structure'
                            )
                        ) : isPointRoi ? (
                            'Move POI'
                        ) : (
                            <Box>
                                <Typography fontSize={11}>
                                    Transform Structure
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: 10,
                                        opacity: 0.7,
                                    }}
                                >
                                    drag to move, structure edge to scale
                                </Typography>
                            </Box>
                        )
                    }
                    selected={isTransformSelected}
                    disabled={isMoveTransformDisabled}
                    onClick={handleTransformSelect}
                    tooltipPlacement="bottom"
                    data-testid="tool-transform"
                />
                <ToolbarButton
                    icon={<LineDeformIcon />}
                    tooltip={tooltipFor('Line deform')}
                    tooltipPlacement="bottom"
                    selected={isLineDeformSelected}
                    disabled={isContouringToolsDisabled}
                    onClick={handleLineDeformSelect}
                    data-testid="tool-line-deform"
                />
                {false && (
                    <ToolbarButton
                        icon={<DeformIcon />}
                        tooltip="Deform"
                        tooltipPlacement="bottom"
                        selected={tool === 'deform'}
                        disabled={isContouringToolsDisabled}
                        onClick={handleDeformSelect}
                    />
                )}
            </Box>
            {tool === 'brush' && (
                <ToolPropertiesTray>
                    <BrushToolOptions />
                </ToolPropertiesTray>
            )}
            {tool === 'line' && (
                <ToolPropertiesTray>
                    <LineToolOptions />
                </ToolPropertiesTray>
            )}
            {tool === 'lineDeform' && (
                <ToolPropertiesTray>
                    <LineDeformToolOptions />
                </ToolPropertiesTray>
            )}
            {false && tool === 'deform' && (
                <ToolPropertiesTray>
                    <DeformToolOptions />
                </ToolPropertiesTray>
            )}
            {tool === 'transform' && isSelectedSupport && hasSiblingSupport && (
                <ToolPropertiesTray>
                    <TransformToolOptions />
                </ToolPropertiesTray>
            )}
        </>
    );
}
